import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  LeafletContent,
  LeafletMedicine,
  LeafletsQuery,
  LeafletVersion,
} from './types';

const KEY = 'medicine-leaflets';

/** GET /medicine-leaflets: active medicines with their approved leaflet and draft. */
export function useLeaflets(query: LeafletsQuery) {
  return useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: async ({ signal }) =>
      (
        await api.get<LeafletMedicine[]>('/medicine-leaflets', {
          signal,
          query: { search: query.search, state: query.state },
        })
      ).data,
  });
}

/** GET /medicine-leaflets/medicines/:id */
export function useLeafletMedicine(medicineId: string | null) {
  return useQuery({
    queryKey: [KEY, 'detail', medicineId],
    queryFn: async ({ signal }) =>
      (
        await api.get<LeafletMedicine>(
          `/medicine-leaflets/medicines/${medicineId}`,
          { signal },
        )
      ).data,
    enabled: !!medicineId,
  });
}

/** GET /medicine-leaflets/medicines/:id/versions: newest first. */
export function useLeafletVersions(medicineId: string | null) {
  return useQuery({
    queryKey: [KEY, 'versions', medicineId],
    queryFn: async ({ signal }) =>
      (
        await api.get<LeafletVersion[]>(
          `/medicine-leaflets/medicines/${medicineId}/versions`,
          { signal },
        )
      ).data,
    enabled: !!medicineId,
  });
}

/** Every mutation answers with the medicine as it now stands. */
function refresh(queryClient: QueryClient, medicine: LeafletMedicine) {
  queryClient.setQueryData([KEY, 'detail', medicine.medicineId], medicine);
  void queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
  void queryClient.invalidateQueries({
    queryKey: [KEY, 'versions', medicine.medicineId],
  });
}

export function useLeafletActions() {
  const queryClient = useQueryClient();
  const onSuccess = (medicine: LeafletMedicine) =>
    refresh(queryClient, medicine);
  return {
    /** POST /medicine-leaflets/medicines/:id/drafts (medicine_leaflets:manage) */
    createDraft: useMutation({
      mutationFn: async (input: {
        medicineId: string;
        content: LeafletContent;
      }) =>
        (
          await api.post<LeafletMedicine>(
            `/medicine-leaflets/medicines/${input.medicineId}/drafts`,
            input.content,
          )
        ).data,
      onSuccess,
    }),
    /** PATCH /medicine-leaflets/:id (medicine_leaflets:manage) */
    saveDraft: useMutation({
      mutationFn: async (input: {
        leafletId: string;
        content: LeafletContent;
      }) =>
        (
          await api.patch<LeafletMedicine>(
            `/medicine-leaflets/${input.leafletId}`,
            input.content,
          )
        ).data,
      onSuccess,
    }),
    /** POST /medicine-leaflets/:id/approve (medicine_leaflets:approve) */
    approve: useMutation({
      mutationFn: async (leafletId: string) =>
        (
          await api.post<LeafletMedicine>(
            `/medicine-leaflets/${leafletId}/approve`,
            {},
          )
        ).data,
      onSuccess,
    }),
    /** POST /medicine-leaflets/:id/retire (medicine_leaflets:approve) */
    retire: useMutation({
      mutationFn: async (leafletId: string) =>
        (
          await api.post<LeafletMedicine>(
            `/medicine-leaflets/${leafletId}/retire`,
            {},
          )
        ).data,
      onSuccess,
    }),
  };
}
